const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escapeAttribute = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const cleanValue = (value) => (typeof value === 'string' ? value.trim() : '');

const APA_ORG_KEYWORDS = [
  'association',
  'institute',
  'department',
  'university',
  'college',
  'ministry',
  'agency',
  'center',
  'centre',
  'organization',
  'organisation',
  'committee',
  'office',
  'foundation',
  'society',
  'channel',
  'archives',
  'corporation',
  'company',
  'inc',
  'ltd',
  'co.'
];

const isOrganizationName = (name) => {
  const cleaned = cleanValue(name);
  if (!cleaned) return false;
  if (cleaned.toUpperCase() === cleaned) return true;
  const lower = cleaned.toLowerCase();
  return APA_ORG_KEYWORDS.some((keyword) => lower.includes(keyword));
};

const splitAuthorList = (rawAuthor = '') => {
  const cleaned = cleanValue(rawAuthor);
  if (!cleaned) return [];
  return cleaned
    .split(/\s*(?:;|&| and )\s*/i)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const parseContributor = (entry = '') => {
  const trimmed = cleanValue(entry);
  if (!trimmed) return null;
  if (isOrganizationName(trimmed)) {
    return { name: trimmed, isOrganization: true };
  }
  if (trimmed.includes(',')) {
    const [last, rest] = trimmed.split(',');
    const first = cleanValue(rest).split(/\s+/)[0] || '';
    return {
      lastName: cleanValue(last),
      firstName: first,
      isOrganization: false
    };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { lastName: parts[0], isOrganization: false };
  }
  return {
    firstName: parts[0],
    lastName: parts[parts.length - 1],
    isOrganization: false
  };
};

const normalizeContributors = (citation = {}) => {
  const contributors = Array.isArray(citation.contributors) ? citation.contributors : [];
  if (contributors.length) {
    return contributors
      .map((contributor) => {
        const name = cleanValue(contributor?.name || contributor?.organization || '');
        const firstName = cleanValue(contributor?.firstName || '');
        const lastName = cleanValue(contributor?.lastName || '');
        if (name) {
          return { name, isOrganization: true };
        }
        if (firstName || lastName) {
          return { firstName, lastName, isOrganization: false };
        }
        return null;
      })
      .filter(Boolean);
  }
  const author = cleanValue(citation.author);
  if (author) {
    return splitAuthorList(author)
      .map((entry) => parseContributor(entry))
      .filter(Boolean);
  }
  const { firstName, lastName } = getCitationAuthorParts(citation);
  if (firstName || lastName) {
    return [{ firstName, lastName, isOrganization: false }];
  }
  return [];
};

export const getCitationAuthorParts = (citation = {}) => {
  const firstName = cleanValue(citation.authorFirstName);
  const lastName = cleanValue(citation.authorLastName);
  if (firstName || lastName) {
    return { firstName, lastName };
  }

  const author = cleanValue(citation.author);
  if (!author) return { firstName: '', lastName: '' };

  if (author.includes(',')) {
    const [last, rest] = author.split(',');
    const remaining = cleanValue(rest);
    const first = remaining ? remaining.split(/\s+/)[0] : '';
    return { firstName: first, lastName: cleanValue(last) };
  }

  const parts = author.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: '', lastName: parts[0] };
  }
  return { firstName: parts[0], lastName: parts[parts.length - 1] };
};

const getAuthorInitial = (firstName) => {
  const cleaned = cleanValue(firstName);
  return cleaned ? `${cleaned[0].toUpperCase()}.` : '';
};

const formatAuthorReferenceName = (citation = {}) => {
  const contributors = normalizeContributors(citation);
  if (!contributors.length) return '';
  const formatted = contributors.map((contributor) => {
    if (contributor.isOrganization) return contributor.name || '';
    const last = cleanValue(contributor.lastName);
    const initial = getAuthorInitial(contributor.firstName);
    return [last, initial].filter(Boolean).join(', ').trim();
  }).filter(Boolean);
  if (!formatted.length) return '';
  if (formatted.length > 20) {
    const firstGroup = formatted.slice(0, 19).join(', ');
    const last = formatted[formatted.length - 1];
    return `${firstGroup}, … ${last}`;
  }
  if (formatted.length === 1) return formatted[0];
  return `${formatted.slice(0, -1).join(', ')}, & ${formatted[formatted.length - 1]}`;
};

const formatAuthorInTextName = (citation = {}) => {
  const contributors = normalizeContributors(citation);
  if (!contributors.length) return '';
  const names = contributors.map((contributor) => {
    if (contributor.isOrganization) return contributor.name || '';
    return contributor.lastName || contributor.firstName || '';
  }).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names[0]} et al.`;
};

const formatApaInTextDate = (dateString, yearString) => {
  const cleanedYear = cleanValue(yearString);
  if (cleanedYear) return cleanedYear;

  const cleanedDate = cleanValue(dateString);
  if (cleanedDate) {
    const parsed = new Date(cleanedDate);
    if (!Number.isNaN(parsed.getTime())) {
      return String(parsed.getFullYear());
    }
  }
  return 'n.d.';
};

export const formatApaDate = (dateString, yearString) => {
  const cleanedDate = cleanValue(dateString);
  if (cleanedDate) {
    const date = new Date(cleanedDate);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  const cleanedYear = cleanValue(yearString);
  return cleanedYear || 'n.d.';
};

const normalizeDoi = (doi) => {
  const cleaned = cleanValue(doi);
  if (!cleaned) return '';
  return cleaned.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '');
};

export const hasApaCitationContent = (citation = {}) => {
  const hasContributors = Array.isArray(citation.contributors)
    ? citation.contributors.some((entry) =>
      cleanValue(entry?.name || entry?.organization || entry?.firstName || entry?.lastName)
    )
    : false;
  const fields = [
    citation.author,
    citation.authorFirstName,
    citation.authorLastName,
    citation.title,
    citation.source,
    citation.publisher,
    citation.siteName,
    citation.version,
    citation.url,
    citation.doi,
    citation.volume,
    citation.pages
  ];
  return hasContributors || fields.some((field) => cleanValue(field));
};

export const getApaCitationKey = (citation = {}) => {
  const type = cleanValue(citation.type) || 'website';
  const keyParts = [
    type,
    cleanValue(citation.author),
    cleanValue(citation.authorFirstName),
    cleanValue(citation.authorLastName),
    ...(Array.isArray(citation.contributors) ? citation.contributors.map((entry) => `${entry?.firstName || ''}${entry?.lastName || ''}${entry?.name || ''}`) : []),
    cleanValue(citation.title),
    cleanValue(citation.source),
    cleanValue(citation.publisher),
    cleanValue(citation.year),
    cleanValue(citation.date),
    cleanValue(citation.url),
    normalizeDoi(citation.doi),
    cleanValue(citation.volume),
    cleanValue(citation.issue),
    cleanValue(citation.pages),
    cleanValue(citation.platform),
    cleanValue(citation.version)
  ];
  return keyParts.join('|').toLowerCase();
};

export const dedupeApaCitations = (citations = []) => {
  const seen = new Set();
  const result = [];

  (citations || []).forEach((citation) => {
    if (!hasApaCitationContent(citation)) return;
    const key = getApaCitationKey(citation);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(citation);
  });

  return result;
};

export const mergeApaCitations = (primary = [], secondary = []) =>
  dedupeApaCitations([...(primary || []), ...(secondary || [])]);

export const getApaCitationSortKey = (citation = {}) => {
  const contributors = normalizeContributors(citation);
  const primaryContributor = contributors[0];
  const primaryName = primaryContributor?.isOrganization
    ? primaryContributor?.name
    : primaryContributor?.lastName || primaryContributor?.firstName || '';
  const title = cleanValue(citation.title);
  const source = cleanValue(citation.source);
  const primary = cleanValue(primaryName) || title || source;
  return [primary, title, source].filter(Boolean).join(' ').toLowerCase();
};

export const sortApaCitations = (citations = []) =>
  [...(citations || [])].sort((a, b) =>
    getApaCitationSortKey(a).localeCompare(getApaCitationSortKey(b))
  );

export const collectApaCitationsFromSections = (sections = []) => {
  const citations = [];
  (sections || []).forEach((section) => {
    (section?.blocks || []).forEach((block) => {
      if (block?.type === 'apa-citation' && Array.isArray(block.citations)) {
        citations.push(...block.citations);
      }
    });
  });
  return citations;
};

const buildLinkHtml = (doi, url) => {
  const normalizedDoi = normalizeDoi(doi);
  const finalUrl = normalizedDoi ? `https://doi.org/${normalizedDoi}` : cleanValue(url);
  if (!finalUrl) return '';
  return `<a href="${escapeAttribute(finalUrl)}" target="_blank" rel="noreferrer">${escapeHtml(finalUrl)}</a>`;
};

export const formatApaQuoteCitation = ({ author, year, title, source }) => {
  const parts = [];
  const cleanedAuthor = cleanValue(author);
  const cleanedYear = cleanValue(year);
  const cleanedTitle = cleanValue(title);
  const cleanedSource = cleanValue(source);

  if (cleanedAuthor) {
    parts.push(cleanedAuthor);
  }

  if (cleanedYear) {
    parts.push(`(${cleanedYear}).`);
  }

  if (cleanedTitle) {
    parts.push(`${cleanedTitle}.`);
  }

  if (cleanedSource) {
    parts.push(`${cleanedSource}.`);
  }

  return parts.join(' ').trim();
};

export const formatApaCitationHtml = (citation = {}) => {
  const type = cleanValue(citation.type) || 'website';
  const author = formatAuthorReferenceName(citation);
  const title = cleanValue(citation.title);
  const source = cleanValue(citation.source);
  const publisher = cleanValue(citation.publisher);
  const version = cleanValue(citation.version);
  const prompt = cleanValue(citation.prompt);
  const notes = cleanValue(citation.notes);
  const dateText = formatApaDate(citation.date, citation.year);
  const volume = cleanValue(citation.volume);
  const issue = cleanValue(citation.issue);
  const pages = cleanValue(citation.pages);
  const linkHtml = buildLinkHtml(citation.doi, citation.url);

  const hasAuthor = Boolean(author);
  const authorSegment = author ? `${escapeHtml(author)}.` : '';
  const dateSegment = dateText ? `(${escapeHtml(dateText)}).` : '';

  const join = (segments) => segments.filter(Boolean).join(' ');

  switch (type) {
    case 'book': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `<em>${escapeHtml(title)}</em>.` : '');
      const titleSegment = hasAuthor && title ? `<em>${escapeHtml(title)}</em>.` : '';
      const sourceSegment = source ? `${escapeHtml(source)}.` : '';
      const pageSegment = pages ? `${escapeHtml(pages)}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, sourceSegment, pageSegment, linkHtml]);
    }
    case 'journal': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `${escapeHtml(title)}.` : '');
      const titleSegment = hasAuthor && title ? `${escapeHtml(title)}.` : '';
      const journalSegment = source ? `<em>${escapeHtml(source)}</em>` : '';
      const volumeIssue = volume ? `${escapeHtml(volume)}${issue ? `(${escapeHtml(issue)})` : ''}` : '';
      const pageSegment = pages ? `${escapeHtml(pages)}` : '';
      const journalDetails = [journalSegment, volumeIssue, pageSegment].filter(Boolean).join(', ');
      const journalSegmentFull = journalDetails ? `${journalDetails}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, journalSegmentFull, linkHtml]);
    }
    case 'video': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `<em>${escapeHtml(title)}</em> [Video].` : '');
      const titleSegment = hasAuthor && title ? `<em>${escapeHtml(title)}</em> [Video].` : '';
      const sourceSegment = source ? `${escapeHtml(source)}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, sourceSegment, linkHtml]);
    }
    case 'software': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `<em>${escapeHtml(title)}</em>.` : '');
      const titleSegment = title ? `<em>${escapeHtml(title)}</em>${version ? ` (${escapeHtml(version)})` : ''} [Computer software].` : '';
      const sourceSegment = publisher ? `${escapeHtml(publisher)}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, sourceSegment, linkHtml]);
    }
    case 'ai': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (publisher ? `${escapeHtml(publisher)}.` : '');
      const toolSegment = source ? `${escapeHtml(source)}${version ? ` (${escapeHtml(version)})` : ''} [Large language model].` : '';
      const promptSegment = prompt || title ? `Prompt: ${escapeHtml(prompt || title)}.` : '';
      const notesSegment = notes ? `${escapeHtml(notes)}.` : '';
      return join([leadingSegment, dateSegment, toolSegment, promptSegment, notesSegment, linkHtml]);
    }
    case 'image': {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `<em>${escapeHtml(title)}</em> [Image].` : '');
      const titleSegment = hasAuthor && title ? `<em>${escapeHtml(title)}</em> [Image].` : '';
      const sourceSegment = source ? `${escapeHtml(source)}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, sourceSegment, linkHtml]);
    }
    case 'website':
    default: {
      const leadingSegment = hasAuthor
        ? authorSegment
        : (title ? `<em>${escapeHtml(title)}</em>.` : '');
      const titleSegment = hasAuthor && title ? `<em>${escapeHtml(title)}</em>.` : '';
      const sourceSegment = source ? `${escapeHtml(source)}.` : '';
      return join([leadingSegment, dateSegment, titleSegment, sourceSegment, linkHtml]);
    }
  }
};

export const formatApaInTextCitationHtml = (citation = {}) => {
  const author = formatAuthorInTextName(citation) || formatAuthorReferenceName(citation);
  const title = cleanValue(citation.title);
  const dateText = formatApaInTextDate(citation.date, citation.year);
  const subject = author ? escapeHtml(author) : (title ? escapeHtml(title) : '');
  const linkHtml = buildLinkHtml(citation.doi, citation.url);
  if (!subject && !dateText) return '';
  const parts = [subject, dateText].filter(Boolean);
  const citationText = `(${parts.join(', ')})`;
  return linkHtml ? `${citationText} ${linkHtml}` : citationText;
};
